<template>
  <div class="container-fluid mt-nav mb-5 mb-chat site-width">
    <DataWrapper ref="itemHandler" :get-data="getItem" #default="{busy, data: item}">
    <div class="d-flex flex-wrap align-items-center mb-5 gap-2">
      <b-breadcrumb :items="breadcrumbs" class="basecrumb d-print-none"/>

      <div class="d-flex flex-wrap gap-2 ml-auto d-print-none">
      <b-button v-b-modal.embed-modal variant="outline-primary">
        Embed
      </b-button>
      <b-modal
        id="embed-modal"
        title="Embed Code"
        size="lg"
        ok-only
        ok-title="Copy to Clipboard"
        @ok="copyToClipboard"
      >
        <b-alert variant="warning" show>Embedding is in beta. Please use for testing only.</b-alert>
        <b-form-textarea
          max-rows="10"
          :value="embedCode"
        ></b-form-textarea>
      </b-modal>

      <DeleteButton
        v-if="canEdit(item)"
        :name="item.name"
        :value="`/api/collections/${item.id}/`"
        @success="onDeleted"
        class="btn-outline-primary-resting"
      />
      <DeleteButtonDisabled
        v-else-if="isStaff"
        title="This collection can only be deleted by its owner"
        button-class="btn-outline-primary-resting"
      />

      <b-button v-if="canEdit(item)" variant="outline-primary" :to="{path: 'edit', query: query, append: true}" replace>
        Edit
      </b-button>
      <span
        v-else-if="isStaff"
        v-b-tooltip
        title="This collection can only be edited by its owner"
        tabindex="0"
      >
        <b-button
          variant="outline-primary"
          disabled
        >Edit</b-button>
      </span>
      </div>
    </div>

    <div v-if="busy" class="text-center">
      <b-spinner small class="text-secondary"/>
    </div>

    <h1 class="mb-0">{{item?.name}}<wbr></h1>
    <div class="whitespace-pre-wrap mt-2">{{ item?.description }}</div>
    <CollectionsInfo v-if="item" :item="item" :user="user" class="text-muted small mt-2 mb-4"/>
    </DataWrapper>

    <DataWrapper ref="resultsHandler" :get-data="getResults_" #default="{busy, data: results}">
    <b-overlay :show="busy" variant="white" spinner-small spinner-variant="secondary">
    <MediaGrid
      :results="results?.images"
      :id.sync="selectedId"
      @replaceState="onReplaceState"
    />
    </b-overlay>
    <Pagination
      v-if="(results?.total > limit) || $route.query.limit || $route.query.offset"
      class="my-4"
      v-show="results"
      :total="results?.total"
      :limit="limit"
      :limit-options="[100, 1000]"
    />
    </DataWrapper>
  </div>
</template>

<script>
import CollectionsInfo, { canEdit } from '../-info.vue'
import DeleteButton from '@/components/DeleteButton.vue'
import DeleteButtonDisabled from '@/components/DeleteButtonDisabled.vue'
import MediaGrid from '@/components/MediaGrid.vue'
import Pagination from '@/components/Pagination.vue'
import { SEARCH_LIMIT_DEFAULT, UI_URL } from '@/services/constants.js'

export default {
  name: 'CollectionsDetail',
  components: {
    CollectionsInfo,
    DeleteButton,
    DeleteButtonDisabled,
    MediaGrid,
    Pagination,
  },
  props: {
    id: String,
    getItem: Function,
    setCache: Function,
    getResults: Function,
    limit: {
      default: SEARCH_LIMIT_DEFAULT,
    },
    offset: null,
  },
  computed: {
    query() {
      return this.$route.query
    },
    breadcrumbs() {
      return [
        {text: '🏠', to: '/'},
        {text: 'Collections', to: {path: '..', append: true}},
        {text: 'Collection', active: true},
      ]
    },
    user() { return this.$store.state.user.user },
    isStaff() { return this.$store.getters['user/isStaff'] },
    selectedId: {
      get() {
        if (!this.$route.hash) { return }
        return this.$route.hash.replace(/^#/, '')
      },
      set(id) {
        const hash = id ? `#${id}` : undefined
        this.$router.replace({...this.$route, hash})
      },
    },
    embedCode() {
      // Prevent "Unterminated template" error by escaping the closing script tag https://stackoverflow.com/a/36607971/490592
      // eslint-disable-next-line no-useless-escape
      return `<script src="${UI_URL}search-results/vue.min.js"><\/script>\n<script src="${UI_URL}search-results/search-results.min.js"><\/script>
<search-results query="?artcollection=${this.id}&limit=${this.limit}"></search-results>`
    },
  },
  watch: {
    limit() {
      this.$refs.resultsHandler.fetchData()
    },
    offset() {
      this.$refs.resultsHandler.fetchData()
    },
    '$store.state.cache.collections': {
      async handler(collections) {
        // Keep page updated when QuickMenu is used
        const collection = collections?.find(i => i.id === Number(this.id))
        const oldVal = this.$refs.itemHandler.data
        if (JSON.stringify(collection) === JSON.stringify(oldVal)) { return }
        await this.setCache(collection)
        await this.$refs.resultsHandler.fetchData()
      },
    },
  },
  methods: {
    getResults_() {
      return this.getResults({
        params: {
          limit: this.limit,
          offset: this.offset,
        },
      })
    },
    onReplaceState(id) {
      const hash = id ? `#${id}` : undefined
      this.$router.replace({...this.$route, hash})
    },
    copyToClipboard() {
      const textarea = document.querySelector('#embed-modal textarea')
      textarea.select()
      document.execCommand('copy')
      this.$bvToast.toast('Copied to clipboard', {variant: 'success', noCloseButton: true, solid: true})
    },
    canEdit(item) {
      return canEdit(item, this.user)
    },
    onDeleted() {
      this.$store.dispatch('cache/delete', 'collections')
      return this.$router.push({path: '..', append: true})
    },
  },
}
</script>
